import { alpha, Box } from "@mui/material";
import type { ReactElement } from "react";
import { ALPHA, BADGE, FONT_SIZE } from "../../../theme/tokens";
import type { SystemTabBadge } from "../Clickable/TabsAndChips/SystemTabs/utils";

/**
 * The count-pill / status-dot indicator that a navigable destination carries — the rendering half of
 * `SystemTabBadge`.
 *
 * Extracted because it was being re-typed per navigation primitive (the tab strip, the side rail, and
 * now the bottom bar), which is how the same "3 pending" pill came to be three independent sets of
 * numbers. One renderer means a count on a tab, a rail item and a bar slot are the same object.
 *
 * Purely presentational: the caller positions it (inline after a label, or absolutely over a glyph).
 */
export const SystemTabBadgeIndicator = ({ badge }: { badge: SystemTabBadge }): ReactElement => {
	const color = badge.color ?? "error";
	return (
		<Box
			component="span"
			aria-label={badge.ariaLabel}
			role={badge.ariaLabel ? "status" : undefined}
			sx={(theme) =>
				badge.dot
					? { width: BADGE.DOT_SIZE_XS, height: BADGE.DOT_SIZE_XS, borderRadius: "50%", backgroundColor: theme.palette[color].main, flexShrink: 0 }
					: {
							display: "inline-flex",
							alignItems: "center",
							justifyContent: "center",
							minWidth: BADGE.PILL_SIZE,
							height: BADGE.PILL_SIZE,
							px: 0.5,
							borderRadius: BADGE.PILL_RADIUS,
							fontSize: FONT_SIZE.LABEL_XS,
							fontWeight: 700,
							lineHeight: 1,
							...(badge.tone === "soft"
								? { backgroundColor: alpha(theme.palette[color].main, ALPHA.CONTROL_TINT_HOVER), color: theme.palette[color].main }
								: { backgroundColor: theme.palette[color].main, color: theme.palette[color].contrastText }),
							flexShrink: 0,
						}
			}
		>
			{badge.dot ? null : badge.content}
		</Box>
	);
};
